import { TeaseRowArticlesEntry } from "frontend/contentful/schema/blocks";
import { TeaseRow } from "design-system/components/blocks/tease-row/tease-row";
import { TeaseStyle } from "design-system/types/types";
import { createComponentHeaderProps } from "frontend/contentful/components/primitives/component-header.props";
import { createContentfulComponent } from "frontend/contentful/lib/create-contentful-component";
import { createUseSearchProps } from "frontend/contentful/components/utils/use-search.props";
import { useSearch } from "frontend/hooks/use-search";
import {
  createModernSearchArticleTease,
  createSearchArticleTease,
} from "frontend/contentful/search/search-article-tease";
import { LegacySearchHit, ModernSearchHit } from "frontend/types";
import { LazyTeaseRowTease } from "design-system/components/blocks/tease-row/tease-row-tease";
import { CreateEditAttributesFn } from "frontend/contentful/lib/edit-attributes-factory";
import { getSiteId } from "frontend/lib/get-site-id";
import { usePageProps } from "frontend/hooks/use-page-props";
import { useContentful } from "frontend/hooks/use-contentful";

interface TeaseRowOptions {
  headerAbove?: boolean;
}

const CuratedContentfulTeaseRowArticles = ({
  entry,
  headerAbove,
  createEditAttributes,
}: {
  entry: TeaseRowArticlesEntry;
  headerAbove: boolean;
  createEditAttributes: CreateEditAttributesFn;
}) => {
  const ids = entry.fields.teasesCurated || [];
  const teases: Array<LazyTeaseRowTease> = [];

  const { data } = useSearch<ModernSearchHit>({
    index: "modern",
    query: ids.map((id) => `id:${id}`).join(" OR "),
    size: entry.fields.teasesCurated?.length || 25,
  });

  /**
   * Sorting hits based on the order of IDs.
   *
   * TODO: Consider updating /api/search/query to accept
   * custom sorting algorithm
   */
  const sortedHits = data?.hits.toSorted((a, b) => {
    return ids.indexOf(a.id) - ids.indexOf(b.id);
  });
  const page = usePageProps();
  const { getEntry } = useContentful();
  const site = getEntry(page.site);
  const siteId = getSiteId(site);

  const headerContent = createComponentHeaderProps(entry.fields.header);

  if (!sortedHits || sortedHits.length === 0) return null;

  sortedHits.forEach((item) => {
    const props = createModernSearchArticleTease(item, siteId, {
      pageProps: page,
    });
    if (props) teases.push({ name: "ArticleTease", props });
  });

  return (
    <TeaseRow
      teases={teases}
      wrap={entry.fields.wrap ? "wrap" : "no-wrap"}
      align={entry.fields.alignment}
      teaseStyle={entry.fields.teaseStyle as TeaseStyle}
      headerContent={headerContent}
      HeadingLevel={headerAbove ? "h3" : "h2"}
      HeadingLevelTease={
        headerAbove
          ? entry.fields.header
            ? "h4"
            : "h3"
          : entry.fields.header
            ? "h3"
            : "h2"
      }
      editAttributes={{
        teases: createEditAttributes({
          entry: entry.fields.articleTeasesQuery,
        }),
      }}
      imagesInset={entry.fields.imagesInset || false}
    />
  );
};

const QueryContentfulTeaseRowArticles = ({
  entry,
  headerAbove,
  createEditAttributes,
}: {
  entry: TeaseRowArticlesEntry;
  headerAbove: boolean;
  createEditAttributes: CreateEditAttributesFn;
}) => {
  const teases: Array<LazyTeaseRowTease> = [];

  const page = usePageProps();
  const { getEntry } = useContentful();
  const site = getEntry(page.site);
  const siteId = getSiteId(site);

  const searchProps = createUseSearchProps(entry.fields.articleTeasesQuery);
  const { data } = useSearch<LegacySearchHit>(searchProps, {
    dedupe: true,
    defaultSize: 3,
  });

  data?.hits.forEach((item) => {
    const props = createSearchArticleTease(item, siteId, {
      thumbnailProps: { width: "486px", height: "308px", aspectRatio: "3/2" },
      pageProps: page,
    });
    if (props) teases.push({ name: "ArticleTease", props });
  });

  return (
    <TeaseRow
      teases={teases}
      wrap={entry.fields.wrap ? "wrap" : "no-wrap"}
      align={entry.fields.alignment}
      teaseStyle={entry.fields.teaseStyle as TeaseStyle}
      headerContent={createComponentHeaderProps(entry.fields.header)}
      HeadingLevel={headerAbove ? "h3" : "h2"}
      HeadingLevelTease={
        headerAbove
          ? entry.fields.header
            ? "h4"
            : "h3"
          : entry.fields.header
            ? "h3"
            : "h2"
      }
      editAttributes={{
        teases: createEditAttributes({
          entry: entry.fields.articleTeasesQuery,
        }),
      }}
      imagesInset={entry.fields.imagesInset || false}
    />
  );
};

export const ContentfulTeaseRowArticles = createContentfulComponent<
  TeaseRowArticlesEntry,
  TeaseRowOptions
>(({ entry, options, createEditAttributes }) => {
  const headerAbove = options?.headerAbove ?? false;

  if (entry.fields.teasesCurated && entry.fields.teasesCurated.length > 0) {
    return (
      <CuratedContentfulTeaseRowArticles
        entry={entry}
        headerAbove={headerAbove}
        createEditAttributes={createEditAttributes}
      />
    );
  }

  if (entry.fields.articleTeasesQuery) {
    return (
      <QueryContentfulTeaseRowArticles
        entry={entry}
        headerAbove={headerAbove}
        createEditAttributes={createEditAttributes}
      />
    );
  }

  return null;
});
